import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Wrapper from '../../components/Wrapper';
import CsrKpiCard from '../../components/CsrDashboard/CsrKpiCard';
import CsrPageHeader from '../../components/CsrDashboard/CsrPageHeader';
import NoDataStateNotice from '../../components/CsrDashboard/NoDataStateNotice';
import { CsrError, CsrLoading } from '../../components/CsrDashboard/CsrStatus';
import { CSR_IMPACT_PLACEHOLDERS } from '../../components/CsrDashboard/csrPlaceholders';
import { fmt, fmtDecimal } from '../../components/CsrDashboard/csrFormat';
import SoilTestsTab, { SoilTestReportModal } from '../../components/CsrDashboard/farmers/SoilTestsTab';
import FarmersTab, { FarmerRecordModal } from '../../components/CsrDashboard/farmers/FarmersTab';
import SoilDidisTab from '../../components/CsrDashboard/farmers/SoilDidisTab';
import { useCsrCoverage, useCsrMetrics } from '../../hooks/useCsrDashboard';
import '../../styles/csrDashboard.css';

const TABS_ID = 'csr-tab';

const CsrFarmers = () => {
  // Support opens /csr/farmers/:adminId; partners and team members use their own login scope.
  const { adminId } = useParams();
  const csr = useCsrCoverage(adminId);
  const { coverageStatus, coverageError, loadCoverage, activeStates, setScope, scopeRow, isStateScope, isNoDataScope, scopeName } = csr;
  const { metrics, metricsStatus } = useCsrMetrics(adminId, coverageStatus === 'success' && !isNoDataScope, scopeRow?.state);

  const [tab, setTab] = useState('tests');
  const [modal, setModal] = useState(null); // { type: 'test', sampleId } | { type: 'farmer', userId }

  const metric = (key) => (metricsStatus === 'success' ? metrics?.[key] : null);
  const show = (key) => (metricsStatus === 'success' ? fmt(metrics?.[key]) : metricsStatus === 'loading' ? '…' : '—');
  const farmers = metric('farmers');
  const area = metric('totalArea');
  const avgPlot = farmers ? `${fmtDecimal(area / farmers)} ac` : metricsStatus === 'loading' ? '…' : '—';
  const impact = CSR_IMPACT_PLACEHOLDERS;
  const scoreGain = impact.soilScore.after - impact.soilScore.before;

  const kpis = [
    { label: 'Farmers reached', value: show('farmers'), sub: 'registered with this partner' },
    { label: 'Soil Didis active', value: show('fieldOfficers'), sub: isStateScope ? `based in ${scopeName}` : 'running village soil testing' },
    { label: 'Average plot size', value: avgPlot, sub: 'acres per farmer' },
    { label: 'Plots retested', value: `${impact.plotsRetestedPct}%`, sub: 'of soil tests', pending: true },
    { label: 'Average soil score gain', value: `${scoreGain >= 0 ? '+' : ''}${scoreGain}`, sub: 'points, before → after', pending: true },
  ];

  const tabs = [
    { key: 'tests', label: `Soil tests · ${show('soilReports')}` },
    { key: 'farmers', label: `Farmers · ${show('farmers')}` },
    { key: 'didis', label: 'Soil Didis & farmer stories' },
  ];

  const onTabKeyDown = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = tabs.findIndex((t) => t.key === tab);
    const next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    setTab(next.key);
    document.getElementById(`${TABS_ID}-${next.key}`)?.focus();
  };

  const selectState = (key) => setScope(key);
  const openTest = (sampleId) => setModal({ type: 'test', sampleId });
  const openFarmer = (userId) => setModal({ type: 'farmer', userId });
  const closeModal = () => setModal(null);
  const stateFilter = scopeRow?.state;
  const panelProps = { adminId, state: stateFilter, scopeName, onOpenTest: openTest, onOpenFarmer: openFarmer };

  return (
    <Wrapper>
      <div className="csr-dash">
        <div className="csr-wrap">
          <CsrPageHeader
            title="Farmers & Soil Didis"
            subtitle="Individual journeys across soil testing and field support"
            csr={csr}
            adminId={adminId}
            onScopeChange={selectState}
          />

          {coverageStatus === 'loading' ? <CsrLoading label="Loading Farmers & Soil Didis" kpis={5} blocks={[56, 520]} /> : null}
          {coverageStatus === 'error' ? <CsrError message={coverageError} onRetry={loadCoverage} /> : null}

          {coverageStatus === 'success' ? (
            <div className="csr-reveal d-flex flex-column" style={{ gap: 20 }}>
              {isNoDataScope ? (
                <NoDataStateNotice stateName={scopeName} activeStateNames={activeStates.map((s) => s.state)} onReset={() => setScope('all')} />
              ) : (
                <>
                  <section aria-label="Farmers and Soil Didis at a glance" className="d-flex flex-column" style={{ gap: 8 }}>
                    <div className="csr-kpis csr-kpis--five">
                      {kpis.map((k) => <CsrKpiCard key={k.label} {...k} />)}
                    </div>
                    {metricsStatus === 'error' ? (
                      <p className="csr-inline-warn">Some figures could not be loaded right now. Refresh the page to try again.</p>
                    ) : null}
                  </section>

                  <div role="tablist" aria-label="Farmers and Soil Didis views" className="csr-tabs" onKeyDown={onTabKeyDown}>
                    {tabs.map((t) => (
                      <button
                        key={t.key}
                        id={`${TABS_ID}-${t.key}`}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.key}
                        aria-controls={`${TABS_ID}-panel-${t.key}`}
                        tabIndex={tab === t.key ? 0 : -1}
                        onClick={() => setTab(t.key)}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>

                  <div role="tabpanel" id={`${TABS_ID}-panel-${tab}`} aria-labelledby={`${TABS_ID}-${tab}`}>
                    {tab === 'tests' ? <SoilTestsTab {...panelProps} /> : null}
                    {tab === 'farmers' ? <FarmersTab {...panelProps} /> : null}
                    {tab === 'didis' ? (
                      <SoilDidisTab {...panelProps} livelihoods={show('fieldOfficers')} score={impact.soilScore} />
                    ) : null}
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <SoilTestReportModal
        adminId={adminId}
        sampleId={modal?.type === 'test' ? modal.sampleId : null}
        onClose={closeModal}
        onOpenFarmer={openFarmer}
      />
      <FarmerRecordModal
        adminId={adminId}
        userId={modal?.type === 'farmer' ? modal.userId : null}
        score={impact.soilScore}
        onClose={closeModal}
        onOpenTest={openTest}
      />
    </Wrapper>
  );
};

export default CsrFarmers;
